import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useGame } from "@/context/GameContext";
import DirectionalText from "@/components/DirectionalText";
import { getTextDirection } from "@/lib/textDirection";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;

// Legacy mapping — used to display old community categories that stored string keys
const GROUP_LABELS = {
  general: "عامّة", science: "علوم", islamic: "ديني", sports: "رياضة",
  anime: "أنمي", series: "مسلسلات", music: "موسيقى", geography: "جغرافيا",
  technology: "تقنية", other: "أخرى",
};
const DIFF_MAP = {
  300: { label: "سهل", color: "#6ee7b7" },
  600: { label: "متوسط", color: "#fcd34d" },
  900: { label: "صعب", color: "#f87171" },
};
const STATUS_MAP = {
  draft:     { label: "مسودة",   color: "#94a3b8", bg: "rgba(148,163,184,0.12)" },
  published: { label: "منشورة",  color: "#6ee7b7", bg: "rgba(110,231,183,0.12)" },
  hidden:    { label: "مخفية",   color: "#f87171", bg: "rgba(248,113,113,0.12)" },
};

/* ── GroupSelect — custom dark dropdown scoped to Creator forms only ────────
   Replaces native <select> for category classification. Native browser
   select/option rendering ignores dark-surface CSS on the open list,
   causing white-on-white readability failures inside HUJJAH's dark UI.
─────────────────────────────────────────────────────────────────────────────*/
function GroupSelect({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function onOutside(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);
  const selected = options.find((o) => o.name === value);
  const label = selected ? (selected.icon ? `${selected.icon} ${selected.name}` : selected.name) : "اختر تصنيفاً";
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", boxSizing: "border-box",
          background: "rgba(255,255,255,0.06)",
          border: open ? "1px solid rgba(242,184,91,0.55)" : "1px solid rgba(255,255,255,0.15)",
          borderRadius: 10, padding: "10px 14px",
          color: "#f8f2e7", fontSize: 14,
          fontFamily: "Cairo, sans-serif", cursor: "pointer",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          outline: "none", textAlign: "right",
        }}
      >
        <span>{label}</span>
        <span style={{ fontSize: 10, opacity: 0.55, marginRight: 4 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)",
          right: 0, left: 0, zIndex: 9999,
          background: "#1a0d24",
          border: "1px solid rgba(242,184,91,0.30)",
          borderRadius: 10, overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0,0,0,0.65)",
          maxHeight: 280, overflowY: "auto",
        }}>
          {options.map((g) => {
            const isSelected = value === g.name;
            return (
              <div
                key={g.id}
                onMouseDown={() => { onChange(g.name); setOpen(false); }}
                style={{
                  padding: "10px 14px", cursor: "pointer",
                  color: isSelected ? "#f2b85b" : "#f0e8d8",
                  background: isSelected ? "rgba(242,184,91,0.14)" : "transparent",
                  fontSize: 14, fontFamily: "Cairo, sans-serif",
                  display: "flex", alignItems: "center", gap: 8,
                  borderBottom: "1px solid rgba(255,255,255,0.05)",
                }}
                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? "rgba(242,184,91,0.14)" : "transparent"; }}
              >
                {g.icon && <span>{g.icon}</span>}
                <span style={{ flex: 1 }}>{g.name}</span>
                {isSelected && <span style={{ color: "#f2b85b", fontSize: 13 }}>✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Background images ───────────────────────────────────────────────────────
   Replace BG_1 / BG_2 / BG_3 with your direct i.pinimg.com image URLs
   when you have them. Current values are dark-premium Pexels images.
─────────────────────────────────────────────────────────────────────────────*/
const BG_1 = "https://i.pinimg.com/736x/a0/be/c2/a0bec239e77e424ecca7f30bfd8a29d1.jpg";
const BG_2 = "https://i.pinimg.com/736x/21/e4/7b/21e47bbf1c1ba178e0a16e50f3565e99.jpg";
const BG_3 = "https://i.pinimg.com/736x/a0/be/c2/a0bec239e77e424ecca7f30bfd8a29d1.jpg";

/* ── Design tokens ─────────────────────────────────────────────────────────── */
const S = {
  page: {
    minHeight: "100vh",
    background: "transparent",
    color: "#f8f2e7",
    fontFamily: "Cairo, sans-serif",
    direction: "rtl",
  },
  content: {
    padding: "0 16px 60px",
  },
  card: {
    background: "rgba(20,12,24,0.82)",
    border: "1px solid rgba(255,255,255,0.13)",
    borderRadius: 22,
    padding: 20,
    boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
    backdropFilter: "blur(18px)",
  },
  statBox: {
    background: "rgba(12,7,18,0.62)",
    border: "1px solid rgba(242,184,91,0.22)",
    borderRadius: 18,
    padding: "18px 20px",
    flex: "1 1 160px",
    backdropFilter: "blur(14px)",
    boxShadow: "0 4px 20px rgba(0,0,0,0.30)",
  },
  statValue: { fontSize: 34, fontWeight: 900, color: "#f2b85b", lineHeight: 1 },
  statLabel: { fontSize: 13, color: "#d8cdb8", marginTop: 6 },
  btnPrimary: {
    background: "linear-gradient(90deg, #f2b85b 0%, #ff8f3d 100%)",
    color: "#1a0f10",
    border: "none",
    borderRadius: 14,
    padding: "14px 20px",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 14,
    fontFamily: "Cairo, sans-serif",
    transition: "transform 0.2s ease, opacity 0.2s ease",
  },
  btnSecondary: {
    background: "rgba(255,255,255,0.08)",
    color: "#f8f2e7",
    border: "1px solid rgba(255,255,255,0.14)",
    borderRadius: 14,
    padding: "14px 20px",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 14,
    fontFamily: "Cairo, sans-serif",
    transition: "transform 0.15s, opacity 0.15s",
  },
  btnDanger: {
    background: "rgba(255,95,109,0.15)",
    color: "#ff8b95",
    border: "1px solid rgba(255,95,109,0.30)",
    borderRadius: 10,
    padding: "7px 14px",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 13,
    fontFamily: "Cairo, sans-serif",
  },
  input: {
    width: "100%",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 10,
    padding: "10px 14px",
    color: "#f8f2e7",
    fontSize: 14,
    outline: "none",
    fontFamily: "Cairo, sans-serif",
    boxSizing: "border-box",
  },
  label: {
    color: "#d8cdb8",
    fontSize: 13,
    fontWeight: 700,
    display: "block",
    marginBottom: 6,
  },
};

/* ── Creator Header — clean, no background images ────────────────────────── */
function CreatorBanner({ user, wallet, onNavigate }) {
  const isPremium = user?.is_premium === true || user?.subscription_type === "premium" || user?.subscription_type === "paid" || (user?.game_credits || 0) > 0;
  return (
    <div style={{
      borderBottom: "1px solid rgba(255,255,255,0.08)",
      background: "linear-gradient(180deg, rgba(9,7,11,0.70) 0%, transparent 100%)",
    }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 24px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <button
            onClick={() => onNavigate("/")}
            style={{ ...S.btnSecondary, padding: "7px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            ← الرئيسية
          </button>
          {isPremium && (
            <span style={{ background: "linear-gradient(90deg,#f2b85b,#ff8f3d)", color: "#1a0f10", borderRadius: 999, padding: "4px 14px", fontSize: 12, fontWeight: 800 }}>
              ⭐ Premium
            </span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
          {/* Avatar */}
          <div style={{
            width: 64, height: 64, borderRadius: "50%",
            background: "linear-gradient(135deg,#f2b85b,#ff8f3d)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 28, fontWeight: 900, flexShrink: 0,
            border: "2px solid rgba(242,184,91,0.40)",
            boxShadow: "0 0 0 4px rgba(242,184,91,0.08)",
          }}>
            {user?.username?.[0]?.toUpperCase() || "؟"}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ margin: "0 0 3px", fontSize: "clamp(18px,3.5vw,24px)", fontWeight: 900, color: "#f8f2e7" }}>
              {user?.username || "مجتمع حُجّة"}
            </h1>
            <p style={{ margin: 0, color: "#94a3b8", fontSize: 13 }}>🏛️ منصة المبدعين</p>
          </div>

          {/* Wallet quick stats */}
          {wallet && (
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {[
                { val: wallet.points_this_month, label: "نقطة" },
                { val: `${wallet.available_balance} ﷼`, label: "رصيد" },
              ].map((item, i) => (
                <div key={i} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: "#f2b85b", lineHeight: 1 }}>{item.val}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{item.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── ImageUploader ─────────────────────────────────────────────────────────── */
function ImageUploader({ label, value, onChange, token }) {
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/upload/image`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("تم رفع الصورة");
    } catch {
      toast.error("فشل رفع الصورة");
    }
  };
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={S.label}>{label}</label>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => e.target.files[0] && upload(e.target.files[0])}
          style={{ fontSize: 13, color: "#d8cdb8", flex: 1 }}
        />
        {value && (
          <img src={value} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover" }} />
        )}
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="أو الصق رابط الصورة"
        style={{ ...S.input, marginTop: 6 }}
      />
    </div>
  );
}

/* ── StatusBadge ───────────────────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || { label: status, color: "#94a3b8", bg: "rgba(148,163,184,0.12)" };
  return (
    <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.color}44`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
      {s.label}
    </span>
  );
}

/* ── Modal wrapper ─────────────────────────────────────────────────────────── */
function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div style={{ ...S.card, maxWidth: 500, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#f2b85b" }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#f8f2e7", fontSize: 22, cursor: "pointer" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ── TabBar ────────────────────────────────────────────────────────────────── */
function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: "flex", gap: 4, marginBottom: 24, background: "rgba(255,255,255,0.06)", borderRadius: 16, padding: 4, overflowX: "auto" }}>
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            flex: "0 0 auto",
            padding: "9px 18px",
            borderRadius: 12,
            border: "none",
            fontWeight: 700,
            fontSize: 14,
            fontFamily: "Cairo, sans-serif",
            cursor: "pointer",
            transition: "all 0.18s",
            background: active === t.id ? "linear-gradient(90deg,#f2b85b,#ff8f3d)" : "transparent",
            color: active === t.id ? "#1a0f10" : "#d8cdb8",
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   DASHBOARD TAB
══════════════════════════════════════════════════════════════════════════════ */
function DashboardTab({ wallet, loading, navigate }) {
  if (loading) return <div style={{ textAlign: "center", padding: 40, color: "#d8cdb8" }}>جاري التحميل...</div>;

  const empty = !wallet;
  const w = wallet || { points_this_month: 0, estimated_sar_this_month: 0, available_balance: 0, total_lifetime_points: 0, total_plays: 0, total_likes: 0, min_withdrawal: 50, conversion_rate: "كل لعبة = نقطة · كل إعجاب = 3 نقاط · 100 نقطة = 1 ريال", pending_payout: null };
  const canWithdraw = w.available_balance >= w.min_withdrawal;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Welcome card */}
      <div style={{ ...S.card, background: "linear-gradient(135deg, rgba(242,184,91,0.15) 0%, rgba(255,143,61,0.08) 100%)", border: "1px solid rgba(242,184,91,0.3)" }}>
        <h2 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 900, color: "#f2b85b" }}>لوحة المبدع 🏆</h2>
        <p style={{ margin: 0, color: "#d8cdb8", fontSize: 14 }}>
          أنشئ فئات تريفيا واكسب نقاطاً عند لعب مجتمع حُجّة لها
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        <div style={S.statBox}>
          <div style={S.statValue}>{w.points_this_month}</div>
          <div style={S.statLabel}>نقاط هذا الشهر</div>
        </div>
        <div style={S.statBox}>
          <div style={S.statValue}>{w.estimated_sar_this_month} ﷼</div>
          <div style={S.statLabel}>تقدير الشهر</div>
        </div>
        <div style={S.statBox}>
          <div style={S.statValue}>{w.available_balance} ﷼</div>
          <div style={S.statLabel}>الرصيد المتاح</div>
        </div>
        <div style={S.statBox}>
          <div style={S.statValue}>{w.total_plays ?? 0}</div>
          <div style={S.statLabel}>إجمالي اللعبات</div>
        </div>
        <div style={S.statBox}>
          <div style={S.statValue}>{w.total_likes ?? 0}</div>
          <div style={S.statLabel}>إجمالي الإعجابات</div>
        </div>
      </div>

      {/* Conversion info */}
      <div style={{ ...S.card, display: "flex", alignItems: "center", gap: 14 }}>
        <span style={{ fontSize: 32 }}>💰</span>
        <div>
          <div style={{ fontWeight: 800, color: "#f2b85b", fontSize: 16 }}>{w.conversion_rate}</div>
          <div style={{ color: "#d8cdb8", fontSize: 13, marginTop: 4 }}>
            الحد الأدنى للسحب: {w.min_withdrawal} ريال
          </div>
        </div>
      </div>

      {/* Pending payout */}
      {w.pending_payout && (
        <div style={{ ...S.card, border: "1px solid rgba(252,211,77,0.3)", background: "rgba(252,211,77,0.06)" }}>
          <div style={{ fontWeight: 800, color: "#fcd34d", marginBottom: 6 }}>⏳ لديك طلب سحب قيد المراجعة</div>
          <div style={{ color: "#d8cdb8", fontSize: 13 }}>
            المبلغ: {w.pending_payout.amount} ﷼ — الحالة: <StatusBadge status={w.pending_payout.status} />
          </div>
        </div>
      )}

      {/* Withdraw CTA */}
      {canWithdraw && !w.pending_payout && (
        <button onClick={() => navigate && navigate("payouts")} style={{ ...S.btnPrimary, alignSelf: "flex-start" }}>
          💸 طلب سحب الأرباح
        </button>
      )}

      {/* How to earn */}
      <div style={S.card}>
        <h3 style={{ margin: "0 0 14px", color: "#f2b85b", fontSize: 16 }}>كيف تكسب النقاط؟</h3>
        {[
          { icon: "🎮", text: "كل مرة يلعب شخص فئتك = نقطة واحدة" },
          { icon: "❤️", text: "كل إعجاب من مشترك = 3 نقاط (المشتركون فقط يقدرون يعجبون)" },
          { icon: "💰", text: "100 نقطة = 1 ريال — الحد الأدنى للسحب 50 ريال" },
          { icon: "🏆", text: "الفئات الأكثر لعبًا وإعجابًا تظهر بالأعلى تلقائياً" },
        ].map((item, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 20 }}>{item.icon}</span>
            <span style={{ color: "#d8cdb8", fontSize: 14 }}>{item.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   MY CATEGORIES TAB
══════════════════════════════════════════════════════════════════════════════ */
function MyCategoriesTab({ token, user, groups }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCat, setSelectedCat] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [qLoading, setQLoading] = useState(false);

  // Default group: "عامّة" if available, else first group
  const defaultGroup = (groups && groups.length > 0)
    ? (groups.find(g => g.name === "عامّة") || groups[0]).name
    : "general";

  // Create form state
  const [newCat, setNewCat] = useState({ name: "", description: "", image_url: "", group: defaultGroup });
  const [creating, setCreating] = useState(false);

  // Add question form state
  const [newQ, setNewQ] = useState({ text: "", answer: "", difficulty: 300, image_url: "", answer_image_url: "" });
  const [addingQ, setAddingQ] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadCats = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/community/categories/mine`, { headers });
      setCats(data);
    } catch {
      toast.error("فشل تحميل الفئات");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadCats(); }, [loadCats]);

  const loadQuestions = async (cat) => {
    setSelectedCat(cat);
    setQLoading(true);
    try {
      const { data } = await axios.get(`${API}/community/categories/${cat.id}/questions`, { headers });
      setQuestions(data);
    } catch {
      toast.error("فشل تحميل الأسئلة");
    } finally {
      setQLoading(false);
    }
  };

  const createCategory = async () => {
    if (!newCat.name.trim()) { toast.error("أدخل اسم الفئة"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/community/categories`, newCat, { headers });
      toast.success("تم إنشاء الفئة");
      setShowCreateModal(false);
      setNewCat({ name: "", description: "", image_url: "", group: defaultGroup });
      loadCats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الإنشاء");
    } finally {
      setCreating(false);
    }
  };

  const deleteCategory = async (catId) => {
    if (!window.confirm("حذف الفئة وجميع أسئلتها؟")) return;
    try {
      await axios.delete(`${API}/community/categories/${catId}`, { headers });
      toast.success("تم الحذف");
      loadCats();
      if (selectedCat?.id === catId) setSelectedCat(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحذف");
    }
  };

  const submitCategory = async (catId) => {
    try {
      await axios.post(`${API}/community/categories/${catId}/submit`, {}, { headers });
      toast.success("🎉 تم نشر فئتك بنجاح!");
      loadCats();
      if (selectedCat?.id === catId) setSelectedCat((prev) => ({ ...prev, status: "published" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل النشر");
    }
  };

  const addQuestion = async () => {
    if (!newQ.text.trim() || !newQ.answer.trim()) { toast.error("أدخل السؤال والإجابة"); return; }
    setAddingQ(true);
    try {
      const { data } = await axios.post(`${API}/community/categories/${selectedCat.id}/questions`, newQ, { headers });
      setQuestions((prev) => [...prev, data]);
      setNewQ({ text: "", answer: "", difficulty: 300, image_url: "", answer_image_url: "" });
      toast.success("تمت إضافة السؤال");
      // Refresh cat to update count
      loadCats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الإضافة");
    } finally {
      setAddingQ(false);
    }
  };

  const deleteQuestion = async (qId) => {
    try {
      await axios.delete(`${API}/community/categories/${selectedCat.id}/questions/${qId}`, { headers });
      setQuestions((prev) => prev.filter((q) => q.id !== qId));
      toast.success("تم الحذف");
    } catch {
      toast.error("فشل الحذف");
    }
  };

  const isPremium = user?.is_premium === true || user?.subscription_type === "premium" || user?.subscription_type === "paid" || (user?.game_credits || 0) > 0;

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: "#d8cdb8" }}>جاري التحميل...</div>;

  // If viewing a category's questions
  if (selectedCat) {
    const canEdit = selectedCat.status !== "hidden";
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <button onClick={() => setSelectedCat(null)} style={{ ...S.btnSecondary, padding: "8px 14px" }}>
            ← رجوع
          </button>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#f2b85b" }}>{selectedCat.name}</h2>
            <StatusBadge status={selectedCat.status} />
          </div>
        </div>

        {selectedCat.status === "rejected" && selectedCat.reject_reason && (
          <div style={{ ...S.card, border: "1px solid rgba(248,113,113,0.3)", background: "rgba(248,113,113,0.06)", marginBottom: 16 }}>
            <div style={{ color: "#f87171", fontWeight: 700, marginBottom: 4 }}>سبب الرفض:</div>
            <div style={{ color: "#d8cdb8", fontSize: 14 }}>{selectedCat.reject_reason}</div>
          </div>
        )}

        {/* Questions list */}
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#f2b85b" }}>
              الأسئلة ({questions.length}/24)
            </h3>
            {selectedCat.status === "draft" && questions.length >= 24 && (
              <button onClick={() => submitCategory(selectedCat.id)} style={{ ...S.btnPrimary, padding: "9px 16px", fontSize: 13 }}>
                🚀 نشر الفئة
              </button>
            )}
          </div>

          {qLoading ? (
            <div style={{ textAlign: "center", padding: 20, color: "#d8cdb8" }}>جاري التحميل...</div>
          ) : questions.length === 0 ? (
            <div style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>لا توجد أسئلة بعد. أضف 24 سؤالاً للتقديم.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {questions.map((q, i) => (
                <div key={q.id} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 12, padding: "12px 16px", display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <span style={{ color: "#94a3b8", fontWeight: 700, fontSize: 13, minWidth: 24 }}>{i + 1}</span>
                  <div style={{ flex: 1 }}>
                    <DirectionalText as="div" style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{q.text}</DirectionalText>
                    <div style={{ color: "#6ee7b7", fontSize: 13 }}>الإجابة: {q.answer}</div>
                    <span style={{ color: DIFF_MAP[q.difficulty]?.color || "#f8f2e7", fontSize: 12, marginTop: 4, display: "inline-block" }}>
                      {DIFF_MAP[q.difficulty]?.label || q.difficulty}
                    </span>
                  </div>
                  {canEdit && (
                    <button onClick={() => deleteQuestion(q.id)} style={S.btnDanger}>حذف</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add question form */}
        {canEdit && (
          <div style={{ ...S.card, marginTop: 16 }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 800, color: "#f2b85b" }}>إضافة سؤال جديد</h3>

            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>نص السؤال *</label>
              <textarea
                value={newQ.text}
                dir={getTextDirection(newQ.text)}
                onChange={(e) => setNewQ((p) => ({ ...p, text: e.target.value }))}
                placeholder="اكتب السؤال هنا..."
                rows={3}
                style={{ ...S.input, resize: "vertical", unicodeBidi: "isolate" }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>الإجابة *</label>
              <input
                value={newQ.answer}
                onChange={(e) => setNewQ((p) => ({ ...p, answer: e.target.value }))}
                placeholder="الإجابة الصحيحة"
                style={S.input}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>الصعوبة</label>
              <div style={{ display: "flex", gap: 8 }}>
                {[300, 600, 900].map((d) => {
                  const active = newQ.difficulty === d;
                  const { label: dlabel, color: dcolor } = DIFF_MAP[d];
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setNewQ((p) => ({ ...p, difficulty: d }))}
                      style={{
                        flex: 1, padding: "10px 6px", borderRadius: 10,
                        border: active ? `2px solid ${dcolor}` : "1px solid rgba(255,255,255,0.15)",
                        background: active ? `${dcolor}22` : "rgba(255,255,255,0.04)",
                        color: active ? dcolor : "#c8bfb0",
                        fontFamily: "Cairo, sans-serif",
                        fontWeight: active ? 800 : 600,
                        fontSize: 13, cursor: "pointer",
                        transition: "all 0.15s", textAlign: "center",
                        outline: "none",
                      }}
                    >
                      <div>{dlabel}</div>
                      <div style={{ fontSize: 11, opacity: 0.78 }}>{d}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <ImageUploader
              label="صورة السؤال (اختياري)"
              value={newQ.image_url}
              onChange={(v) => setNewQ((p) => ({ ...p, image_url: v }))}
              token={token}
            />

            <ImageUploader
              label="صورة الإجابة (اختياري)"
              value={newQ.answer_image_url}
              onChange={(v) => setNewQ((p) => ({ ...p, answer_image_url: v }))}
              token={token}
            />

            <button onClick={addQuestion} disabled={addingQ} style={{ ...S.btnPrimary, width: "100%", marginTop: 8 }}>
              {addingQ ? "جاري الإضافة..." : "➕ إضافة السؤال"}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: "#f2b85b" }}>فئاتي</h2>
        {isPremium ? (
          <button onClick={() => setShowCreateModal(true)} style={S.btnPrimary}>
            ➕ فئة جديدة
          </button>
        ) : (
          <div style={{ color: "#fcd34d", fontSize: 13, textAlign: "center" }}>
            🔒 إنشاء الفئات للمشتركين Premium
          </div>
        )}
      </div>

      {/* Premium upgrade prompt */}
      {!isPremium && (
        <div style={{ ...S.card, border: "1px solid rgba(252,211,77,0.3)", background: "rgba(252,211,77,0.06)", marginBottom: 20, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>⭐</div>
          <h3 style={{ margin: "0 0 8px", color: "#fcd34d" }}>اشترك في Premium لإنشاء فئاتك</h3>
          <p style={{ color: "#d8cdb8", fontSize: 14, margin: "0 0 14px" }}>
            أنشئ فئات تريفيا خاصة بك واكسب أرباحاً من كل لعبة
          </p>
        </div>
      )}

      {/* Categories list */}
      {cats.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📁</div>
          <div style={{ color: "#94a3b8", fontSize: 15 }}>لا توجد فئات بعد</div>
          {isPremium && (
            <button onClick={() => setShowCreateModal(true)} style={{ ...S.btnPrimary, marginTop: 16 }}>
              أنشئ أول فئة
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {cats.map((cat) => (
            <div key={cat.id} style={{ ...S.card, cursor: "pointer" }} onClick={() => loadQuestions(cat)}>
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                {cat.image_url && (
                  <img src={cat.image_url} alt="" style={{ width: 60, height: 60, borderRadius: 12, objectFit: "cover", flexShrink: 0 }} />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 800 }}>{cat.name}</h3>
                    <StatusBadge status={cat.status} />
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 8 }}>
                    {GROUP_LABELS[cat.group] || cat.group} • {cat.question_count || 0} سؤال • {cat.plays || 0} لعبة • ❤️ {cat.likes || 0}
                  </div>
                  {cat.description && (
                    <div style={{ color: "#d8cdb8", fontSize: 13, marginBottom: 8 }}>{cat.description}</div>
                  )}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} onClick={(e) => e.stopPropagation()}>
                    {cat.status === "draft" && (cat.question_count || 0) >= 18 && (
                      <button onClick={() => submitCategory(cat.id)} style={{ ...S.btnPrimary, padding: "7px 14px", fontSize: 13 }}>
                        🚀 نشر الفئة
                      </button>
                    )}
                    {cat.status === "draft" && (
                      <button onClick={() => deleteCategory(cat.id)} style={S.btnDanger}>
                        🗑️ حذف
                      </button>
                    )}
                    <button onClick={() => loadQuestions(cat)} style={{ ...S.btnSecondary, padding: "7px 14px", fontSize: 13 }}>
                      📝 الأسئلة
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Category Modal */}
      {showCreateModal && (
        <Modal title="إنشاء فئة جديدة" onClose={() => setShowCreateModal(false)}>
          <div style={{ marginBottom: 12 }}>
            <label style={S.label}>اسم الفئة *</label>
            <input
              value={newCat.name}
              onChange={(e) => setNewCat((p) => ({ ...p, name: e.target.value }))}
              placeholder="مثال: تاريخ الدول العربية"
              style={S.input}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={S.label}>الوصف</label>
            <textarea
              value={newCat.description}
              onChange={(e) => setNewCat((p) => ({ ...p, description: e.target.value }))}
              placeholder="وصف مختصر للفئة..."
              rows={3}
              style={{ ...S.input, resize: "vertical" }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={S.label}>التصنيف</label>
            <GroupSelect
              value={newCat.group}
              onChange={(v) => setNewCat((p) => ({ ...p, group: v }))}
              options={groups || []}
            />
          </div>

          <ImageUploader
            label="صورة الفئة"
            value={newCat.image_url}
            onChange={(v) => setNewCat((p) => ({ ...p, image_url: v }))}
            token={token}
          />

          <button onClick={createCategory} disabled={creating} style={{ ...S.btnPrimary, width: "100%", marginTop: 8 }}>
            {creating ? "جاري الإنشاء..." : "✅ إنشاء الفئة"}
          </button>
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   DISCOVER TAB
══════════════════════════════════════════════════════════════════════════════ */
function DiscoverTab({ token, user, groups }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("all");
  const [sort, setSort] = useState("trending");
  const [skip, setSkip] = useState(0);
  const [likedIds, setLikedIds] = useState(new Set());
  const LIMIT = 20;

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async (reset = false) => {
    setLoading(true);
    const currentSkip = reset ? 0 : skip;
    try {
      const params = new URLSearchParams({ sort, skip: currentSkip, limit: LIMIT });
      if (search) params.set("q", search);
      if (group !== "all") params.set("group", group);
      const { data } = await axios.get(`${API}/community/discover?${params}`, {
        headers: token ? headers : {},
      });
      const newItems = data.items || [];
      setItems(reset ? newItems : (prev) => [...prev, ...newItems]);
      setTotal(data.total);
      if (!reset) setSkip(currentSkip + LIMIT);
      // Sync like state from server response
      setLikedIds(prev => {
        const n = reset ? new Set() : new Set(prev);
        newItems.forEach(c => { if (c.user_liked) n.add(c.id); });
        return n;
      });
    } catch {
      toast.error("فشل تحميل الاكتشاف");
    } finally {
      setLoading(false);
    }
  }, [search, group, sort, skip, token]);

  useEffect(() => {
    setSkip(0);
    setItems([]);
    load(true);
  }, [search, group, sort]);

  const toggleLike = async (catId) => {
    if (!token) { toast.error("يجب تسجيل الدخول"); return; }
    try {
      const { data } = await axios.post(`${API}/community/categories/${catId}/like`, {}, { headers });
      setLikedIds((prev) => {
        const n = new Set(prev);
        data.liked ? n.add(catId) : n.delete(catId);
        return n;
      });
      setItems((prev) => prev.map((c) => c.id === catId ? { ...c, likes: c.likes + (data.liked ? 1 : -1) } : c));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل");
    }
  };

  const sortTabs = [
    { id: "trending", label: "🔥 رائج" },
    { id: "most_played", label: "🎮 الأكثر لعباً" },
    { id: "top_liked", label: "❤️ الأكثر إعجاباً" },
    { id: "new", label: "✨ الأحدث" },
  ];

  return (
    <div>
      {/* Search */}
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="🔍 ابحث عن فئات..."
        style={{ ...S.input, marginBottom: 14 }}
      />

      {/* Group chips */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, overflowX: "auto", paddingBottom: 4 }}>
        <button
          onClick={() => setGroup("all")}
          style={{
            flexShrink: 0,
            padding: "6px 16px",
            borderRadius: 20,
            border: "none",
            fontWeight: 700,
            fontSize: 13,
            fontFamily: "Cairo, sans-serif",
            cursor: "pointer",
            background: group === "all" ? "#f2b85b" : "rgba(255,255,255,0.08)",
            color: group === "all" ? "#1a0f10" : "#d8cdb8",
          }}
        >
          الكل
        </button>
        {(groups && groups.length > 0 ? groups : []).map((g) => (
          <button
            key={g.id}
            onClick={() => setGroup(g.name)}
            style={{
              flexShrink: 0,
              padding: "6px 16px",
              borderRadius: 20,
              border: "none",
              fontWeight: 700,
              fontSize: 13,
              fontFamily: "Cairo, sans-serif",
              cursor: "pointer",
              background: group === g.name ? "#f2b85b" : "rgba(255,255,255,0.08)",
              color: group === g.name ? "#1a0f10" : "#d8cdb8",
            }}
          >
            {g.icon ? `${g.icon} ${g.name}` : g.name}
          </button>
        ))}
      </div>

      {/* Sort tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, overflowX: "auto" }}>
        {sortTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setSort(t.id)}
            style={{
              flexShrink: 0,
              padding: "7px 14px",
              borderRadius: 10,
              border: `1px solid ${sort === t.id ? "#f2b85b" : "rgba(255,255,255,0.12)"}`,
              fontWeight: 700,
              fontSize: 13,
              fontFamily: "Cairo, sans-serif",
              cursor: "pointer",
              background: sort === t.id ? "rgba(242,184,91,0.15)" : "transparent",
              color: sort === t.id ? "#f2b85b" : "#d8cdb8",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Results count */}
      <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 14 }}>
        {total} فئة
      </div>

      {/* Grid */}
      {loading && items.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#d8cdb8" }}>جاري التحميل...</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>لا توجد فئات منشورة بعد</div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 16 }}>
            {items.map((cat) => (
              <div key={cat.id} style={{ ...S.card, padding: 0, overflow: "hidden" }}>
                {cat.image_url ? (
                  <img src={cat.image_url} alt="" style={{ width: "100%", height: 130, objectFit: "cover" }} />
                ) : (
                  <div style={{ width: "100%", height: 80, background: "rgba(242,184,91,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36 }}>
                    🎲
                  </div>
                )}
                <div style={{ padding: 14 }}>
                  <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 800 }}>{cat.name}</h3>
                  <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 8 }}>
                    بقلم {cat.creator_name}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", gap: 10, fontSize: 13, color: "#d8cdb8" }}>
                      <span>🎮 {cat.plays || 0}</span>
                      <span>❤️ {cat.likes || 0}</span>
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{ background: "rgba(242,184,91,0.12)", color: "#f2b85b", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
                        {GROUP_LABELS[cat.group] || cat.group}
                      </span>
                      <button
                        onClick={() => toggleLike(cat.id)}
                        style={{
                          background: likedIds.has(cat.id) ? "rgba(248,113,113,0.2)" : "rgba(255,255,255,0.06)",
                          border: `1px solid ${likedIds.has(cat.id) ? "#f87171" : "rgba(255,255,255,0.12)"}`,
                          borderRadius: 8,
                          padding: "4px 10px",
                          cursor: "pointer",
                          fontSize: 14,
                          color: likedIds.has(cat.id) ? "#f87171" : "#d8cdb8",
                        }}
                      >
                        {likedIds.has(cat.id) ? "❤️" : "🤍"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Load more */}
          {items.length < total && (
            <div style={{ textAlign: "center", marginTop: 20 }}>
              <button onClick={() => load(false)} disabled={loading} style={S.btnSecondary}>
                {loading ? "جاري التحميل..." : "تحميل المزيد"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   PAYOUTS TAB
══════════════════════════════════════════════════════════════════════════════ */
function PayoutsTab({ token, wallet }) {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ amount: "", iban: "", account_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/community/payouts`, { headers });
        setPayouts(data);
      } catch {
        toast.error("فشل تحميل المدفوعات");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const submit = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount < 50) { toast.error("الحد الأدنى 50 ريال"); return; }
    if (!form.iban.trim()) { toast.error("أدخل رقم الآيبان"); return; }
    if (!form.account_name.trim()) { toast.error("أدخل اسم صاحب الحساب"); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/community/payouts`, { amount, iban: form.iban, account_name: form.account_name }, { headers });
      setPayouts((prev) => [data, ...prev]);
      setForm({ amount: "", iban: "", account_name: "" });
      toast.success("تم إرسال طلب السحب!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الإرسال");
    } finally {
      setSubmitting(false);
    }
  };

  const PAYOUT_STATUS = {
    pending:      { label: "قيد الانتظار", color: "#94a3b8" },
    under_review: { label: "تحت المراجعة", color: "#fcd34d" },
    approved:     { label: "موافق عليه", color: "#6ee7b7" },
    rejected:     { label: "مرفوض",      color: "#f87171" },
    paid:         { label: "مدفوع",       color: "#a78bfa" },
  };

  const canWithdraw = wallet && wallet.available_balance >= 50 && !wallet.pending_payout;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Wallet summary */}
      {wallet && (
        <div style={{ ...S.card, display: "flex", gap: 20, flexWrap: "wrap" }}>
          <div style={S.statBox}>
            <div style={S.statValue}>{wallet.available_balance} ﷼</div>
            <div style={S.statLabel}>الرصيد المتاح</div>
          </div>
          <div style={S.statBox}>
            <div style={{ ...S.statValue, fontSize: 20 }}>{wallet.min_withdrawal} ﷼</div>
            <div style={S.statLabel}>الحد الأدنى للسحب</div>
          </div>
          <div style={S.statBox}>
            <div style={{ ...S.statValue, fontSize: 20 }}>{wallet.total_paid} ﷼</div>
            <div style={S.statLabel}>إجمالي المسحوب</div>
          </div>
        </div>
      )}

      {/* Withdrawal form */}
      {canWithdraw ? (
        <div style={S.card}>
          <h3 style={{ margin: "0 0 16px", color: "#f2b85b", fontSize: 16 }}>طلب سحب أرباح</h3>

          <div style={{ marginBottom: 12 }}>
            <label style={S.label}>المبلغ (ريال) *</label>
            <input
              type="number"
              value={form.amount}
              onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
              placeholder="الحد الأدنى 50 ريال"
              min={50}
              max={wallet?.available_balance}
              style={S.input}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={S.label}>رقم الآيبان *</label>
            <input
              value={form.iban}
              onChange={(e) => setForm((p) => ({ ...p, iban: e.target.value }))}
              placeholder="SA..."
              style={S.input}
              dir="ltr"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={S.label}>اسم صاحب الحساب *</label>
            <input
              value={form.account_name}
              onChange={(e) => setForm((p) => ({ ...p, account_name: e.target.value }))}
              placeholder="الاسم كما في الحساب البنكي"
              style={S.input}
            />
          </div>

          <button onClick={submit} disabled={submitting} style={{ ...S.btnPrimary, width: "100%" }}>
            {submitting ? "جاري الإرسال..." : "💸 طلب السحب"}
          </button>
        </div>
      ) : wallet?.pending_payout ? (
        <div style={{ ...S.card, border: "1px solid rgba(252,211,77,0.3)", background: "rgba(252,211,77,0.06)", textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
          <div style={{ color: "#fcd34d", fontWeight: 700 }}>لديك طلب سحب قيد المراجعة</div>
        </div>
      ) : (
        <div style={{ ...S.card, textAlign: "center", color: "#94a3b8" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>💰</div>
          <div>تحتاج رصيداً لا يقل عن 50 ريال للسحب</div>
          <div style={{ color: "#d8cdb8", fontSize: 13, marginTop: 6 }}>رصيدك الحالي: {wallet?.available_balance || 0} ﷼</div>
        </div>
      )}

      {/* Payouts history */}
      <div style={S.card}>
        <h3 style={{ margin: "0 0 16px", color: "#f2b85b", fontSize: 16 }}>سجل المدفوعات</h3>

        {loading ? (
          <div style={{ textAlign: "center", padding: 20, color: "#d8cdb8" }}>جاري التحميل...</div>
        ) : payouts.length === 0 ? (
          <div style={{ textAlign: "center", padding: 20, color: "#94a3b8" }}>لا توجد طلبات سابقة</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                  {["التاريخ", "المبلغ", "الآيبان", "الحالة", "ملاحظة"].map((h) => (
                    <th key={h} style={{ padding: "8px 10px", textAlign: "right", color: "#94a3b8", fontWeight: 700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => {
                  const st = PAYOUT_STATUS[p.status] || { label: p.status, color: "#94a3b8" };
                  return (
                    <tr key={p.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <td style={{ padding: "10px", color: "#d8cdb8" }}>{p.requested_at?.slice(0, 10)}</td>
                      <td style={{ padding: "10px", color: "#f2b85b", fontWeight: 700 }}>{p.amount} ﷼</td>
                      <td style={{ padding: "10px", color: "#94a3b8", direction: "ltr" }}>{p.iban}</td>
                      <td style={{ padding: "10px" }}>
                        <span style={{ color: st.color, fontWeight: 700, fontSize: 13 }}>{st.label}</span>
                      </td>
                      <td style={{ padding: "10px", color: "#94a3b8", fontSize: 13 }}>{p.admin_note || "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   NOTIFICATIONS TAB
══════════════════════════════════════════════════════════════════════════════ */
function NotificationsTab({ token }) {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/community/notifications`, { headers });
      setNotifs(data);
    } catch {
      toast.error("فشل تحميل الإشعارات");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/community/notifications/read`, {}, { headers });
      setNotifs((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success("تم تحديد الكل كمقروء");
    } catch {
      toast.error("فشل");
    }
  };

  const NOTIF_ICONS = {
    category_approved: "✅",
    category_rejected: "❌",
    payout_approved: "👍",
    payout_rejected: "❌",
    payout_paid: "💸",
  };

  const unreadCount = notifs.filter((n) => !n.is_read).length;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: "#f2b85b" }}>
          الإشعارات {unreadCount > 0 && (
            <span style={{ background: "#f87171", color: "#fff", borderRadius: 20, padding: "2px 8px", fontSize: 13, marginRight: 8 }}>
              {unreadCount}
            </span>
          )}
        </h2>
        {unreadCount > 0 && (
          <button onClick={markAllRead} style={{ ...S.btnSecondary, padding: "8px 14px", fontSize: 13 }}>
            تحديد الكل كمقروء
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#d8cdb8" }}>جاري التحميل...</div>
      ) : notifs.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔔</div>
          <div style={{ color: "#94a3b8" }}>لا توجد إشعارات</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {notifs.map((n) => (
            <div
              key={n.id}
              style={{
                ...S.card,
                padding: "14px 18px",
                borderRight: `3px solid ${n.is_read ? "transparent" : "#f2b85b"}`,
                background: n.is_read ? "rgba(20,12,24,0.6)" : "rgba(242,184,91,0.06)",
              }}
            >
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ fontSize: 22, flexShrink: 0 }}>{NOTIF_ICONS[n.type] || "🔔"}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: n.is_read ? 400 : 700, fontSize: 14, marginBottom: 4 }}>
                    {n.message}
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: 12 }}>
                    {n.created_at?.slice(0, 16).replace("T", " ") || ""}
                  </div>
                </div>
                {!n.is_read && (
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f2b85b", flexShrink: 0, marginTop: 6 }} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN COMMUNITY PAGE
══════════════════════════════════════════════════════════════════════════════ */
export default function CommunityPage() {
  const navigate = useNavigate();
  const { userToken: token, currentUser: user } = useGame();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [notifCount, setNotifCount] = useState(0);
  const [groups, setGroups] = useState([]);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Load category groups (for create form + discover filter)
  useEffect(() => {
    axios.get(`${API}/category-groups`)
      .then(({ data }) => setGroups(data))
      .catch(() => {});
  }, []);

  // Load wallet & notification count on mount
  useEffect(() => {
    if (!token) return;
    const loadWallet = async () => {
      try {
        const { data } = await axios.get(`${API}/community/wallet`, { headers });
        setWallet(data);
      } catch {
        // Not logged in or error — ignore
      } finally {
        setWalletLoading(false);
      }
    };
    const loadNotifCount = async () => {
      try {
        const { data } = await axios.get(`${API}/community/notifications`, { headers });
        setNotifCount(data.filter((n) => !n.is_read).length);
      } catch {}
    };
    loadWallet();
    loadNotifCount();
  }, [token]);

  const goToPayouts = (tab) => setActiveTab(tab);

  // If not logged in — hero with layered backgrounds
  if (!token) {
    return (
      <div style={S.page}>
        <div style={{ position:"fixed", inset:0, zIndex:1, pointerEvents:"none" }}>
          <div style={{ position:"absolute", top:0, left:0, width:"50%", height:"100%", backgroundImage:`url("${BG_1}")`, backgroundSize:"cover", backgroundPosition:"center", filter:"brightness(0.72) saturate(1.05)" }} />
          <div style={{ position:"absolute", top:0, right:0, width:"50%", height:"100%", backgroundImage:`url("${BG_2}")`, backgroundSize:"cover", backgroundPosition:"center", filter:"brightness(0.72) saturate(1.05)" }} />
          <div style={{ position:"fixed", top:0, left:"20%", width:"60%", height:"100%", background:"linear-gradient(90deg, transparent 0%, #000 40%, #000 60%, transparent 100%)", zIndex:3, pointerEvents:"none" }} />
        <div style={{ position:"fixed", bottom:0, left:0, width:"100%", height:"40%", background:"linear-gradient(to top, rgba(0,0,0,0.95) 0%, transparent 100%)", zIndex:3, pointerEvents:"none" }} />
        </div>
        {/* Content layer */}
        <div style={{ position: "relative", zIndex: 2 }}>
        {/* Hero banner */}
        <div style={{
          position: "relative", overflow: "hidden", minHeight: "60vh",
          display: "flex", alignItems: "center", justifyContent: "center",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}>
          <div style={{
            position: "absolute", inset: 0,
            background: "radial-gradient(ellipse at 50% 30%, rgba(242,184,91,0.12) 0%, transparent 65%), linear-gradient(180deg, rgba(9,7,11,0.0) 0%, #120d14 100%)",
            pointerEvents: "none",
          }} />
          <div style={{ position: "relative", textAlign: "center", padding: "60px 24px 80px", maxWidth: 520 }}>
            <div style={{ fontSize: 64, marginBottom: 20, filter: "drop-shadow(0 0 24px rgba(242,184,91,0.40))" }}>🏛️</div>
            <h1 style={{ fontSize: "clamp(26px,5vw,38px)", fontWeight: 900, color: "#f8f2e7", margin: "0 0 14px", lineHeight: 1.2 }}>
              مجتمع <span style={{ color: "#f2b85b" }}>حُجّة</span>
            </h1>
            <p style={{ color: "#d8cdb8", fontSize: 16, marginBottom: 36, lineHeight: 1.7 }}>
              أنشئ فئات تريفيا خاصة بك، شاركها مع المجتمع، واكسب أرباحاً حقيقية
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button onClick={() => navigate("/login")} style={{ ...S.btnPrimary, padding: "14px 32px" }}>تسجيل الدخول</button>
              <button onClick={() => navigate("/signup")} style={{ ...S.btnSecondary, padding: "14px 32px" }}>إنشاء حساب</button>
            </div>
            <button onClick={() => navigate("/")} style={{ ...S.btnSecondary, marginTop: 14, padding: "10px 20px", fontSize: 13 }}>
              ← الرجوع للرئيسية
            </button>
          </div>
        </div>
        </div>{/* /content zIndex:1 */}
      </div>
    );
  }

  const TABS = [
    { id: "dashboard",     label: "📊 لوحتي" },
    { id: "my-categories", label: "📁 فئاتي" },
    { id: "discover",      label: "🌍 اكتشاف" },
    { id: "payouts",       label: "💸 المدفوعات" },
    { id: "notifications", label: `🔔 الإشعارات${notifCount > 0 ? ` (${notifCount})` : ""}` },
  ];

  return (
    <div style={S.page}>
      <div style={{ position:"fixed", inset:0, zIndex:1, pointerEvents:"none" }}>
        <div style={{ position:"absolute", top:0, left:0, width:"50%", height:"100%", backgroundImage:`url("${BG_1}")`, backgroundSize:"cover", backgroundPosition:"center", filter:"brightness(0.72) saturate(1.05)" }} />
        <div style={{ position:"absolute", top:0, right:0, width:"50%", height:"100%", backgroundImage:`url("${BG_2}")`, backgroundSize:"cover", backgroundPosition:"center", filter:"brightness(0.72) saturate(1.05)" }} />
        <div style={{ position:"fixed", top:0, left:"20%", width:"60%", height:"100%", background:"linear-gradient(90deg, transparent 0%, #000 40%, #000 60%, transparent 100%)", zIndex:3, pointerEvents:"none" }} />
        <div style={{ position:"fixed", bottom:0, left:0, width:"100%", height:"40%", background:"linear-gradient(to top, rgba(0,0,0,0.95) 0%, transparent 100%)", zIndex:3, pointerEvents:"none" }} />
      </div>
      {/* Content layer */}
      <div style={{ position: "relative", zIndex: 2 }}>
        {/* ── Creator header — clean, no images ── */}
        <CreatorBanner user={user} wallet={wallet} onNavigate={navigate} />

        <div style={{ maxWidth: 900, margin: "0 auto", ...S.content, paddingTop: 28 }}>
          {/* Tab bar */}
          <TabBar tabs={TABS} active={activeTab} onChange={setActiveTab} />

          {/* Tab content */}
          {activeTab === "dashboard" && (
            <DashboardTab wallet={wallet} loading={walletLoading} navigate={goToPayouts} />
          )}
          {activeTab === "my-categories" && (
            <MyCategoriesTab token={token} user={user} groups={groups} />
          )}
          {activeTab === "discover" && (
            <DiscoverTab token={token} user={user} groups={groups} />
          )}
          {activeTab === "payouts" && (
            <PayoutsTab token={token} wallet={wallet} />
          )}
          {activeTab === "notifications" && (
            <NotificationsTab token={token} />
          )}
        </div>
      </div>
    </div>
  );
}
